import React, { useContext, useEffect, useState } from "react";
import { Context } from "../../state/context";
import useFetch from "../../api/fetch";
import { ROLE } from '../../enums/roles';
import { hasRole } from '../../utils/hasRole';
import { comparer } from '../../utils/comparer';
import { CONFIG } from '../../config';

import TranslationItemDetails from '../translation-item-details/translation-item-details.components';
import TranslationForm from '../translation-form/translation-form.components';
import PreviousTranslations from '../previous-translations/previous-translations.component';
import PreviousTranslationsList from '../previous-translations-list/previous-translations-list.component';
import CommentSection from '../comment-section/comment-section.components';
import TranslationCarousel from '../translation-carousel/translation-carousel.components';

import './keys-translation-item.styles.scss';
import TagsSelect from "../tags-select/tags-select.component";
import { KEY_STATUS } from "../../enums/key-status";
import {useParams} from "react-router-dom";
import useGetKeys from "../../api/keys";

const KEY_OPERATIONS = {
    PUBLISH: 1,
    DRAFT: 2,
    OBSOLETE: 3
};

export default function KeysTranslationItem({ item }) {
    const { state, dispatch } = useContext(Context);
    const { language: languageTag } = useParams();
    const fetch = useFetch();
    const getKeys = useGetKeys();

    const { key } = item;

    const [auditList, setAuditList] = useState([]);
    const [previousTranslations, setPreviousTranslations] = useState(false);
    const [previousTranslationsUpdate, setPreviousTranslationsUpdate] = useState([]);
    const [tags, setTags] = useState(false);
    const [tagsUpdate, setTagsUpdate] = useState([]);
    const [comments, setComments] = useState([]);
    const [images, setImages] = useState([]);
    const [sourceText, setSourceText] = useState("");
    const [indicationText, setIndicationText] = useState("");
    const [roles, setRoles] = useState([]);
    const [text, setText] = useState("");
    const [value, setValue] = useState({});
    const [actionToTake, setActionToTake] = useState(0);
    const [error, setError] = useState(null);
    const [submitLoader, setSubmitLoader] = useState(false);

    useEffect(() => {
        getImages().then(newImages => { setImages(newImages) });
    }, [key]);

    useEffect(() => {
        setText('');
        setComments([]);
        setAuditList([]);
        getKeyValue().then(newValue => {
            setValue(newValue);
            setText(newValue.target);
            getComments(newValue).then(commentsFeed => { setComments(commentsFeed) });
            getTranslationValueAudit(newValue).then(auditList => setAuditList(auditList));
        })
    }, [state.language]);

    useEffect(() => {
        getKeyValue().then(newValue => {
            setValue(newValue);
            setText(newValue.target);
            setSourceText(newValue.source_string);
            setIndicationText(newValue.indicationText);
            getPreviousTranslations().then(previousTranslations => {
                const mappedPreviousTranslations = previousTranslations.map((previousTranslation) => {
                    return {
                        'value': previousTranslation.id,
                        'label': previousTranslation.original_text,
                        'localized_text': previousTranslation.localized_text
                    }
                });
                setPreviousTranslations(mappedPreviousTranslations);
                setPreviousTranslationsUpdate(mappedPreviousTranslations);
            });
            getTags().then(tags => {
                const mappedTags = tags.map((t) => {
                    return {
                        'value': t.id,
                        'label': t.name
                    }
                });
                setTags(mappedTags);
                setTagsUpdate(mappedTags);
            })
            getComments(newValue).then(commentsFeed => { setComments(commentsFeed) });
            getTranslationValueAudit(newValue).then(auditList => setAuditList(auditList));
        });
    }, [key]);

    useEffect(() => {
        setRoles(state.roles)
    }, [state.roles]);

    const handleSave = async (string, status) => {
        setError(null);
        const data = new URLSearchParams();

        data.append("text", string);
        data.append("status", status);

        const result = await fetch(`/api/value/update/${value.id}`, {
            method: "PUT",
            body: data
        })
        .then(x => x.json())
        .catch(errors => {
            console.log(errors);
        });

        if (result.id) {
            setValue({
                ...value,
                target: result.text,
                status: result.status
            });
            setText(result.text);

            dispatch({ type: 'UPDATE_KEYS', newKey: result });
        } else {
            setError(result[0]);
            setText(value.target);
        }

        return result;

    };

    const handlePreviousTranslationsSave = async () => {
        const newIds = previousTranslationsUpdate.map((translation) => translation.value);
        const oldIds = previousTranslations.map((translation) => translation.value);

        const addedIds = newIds.filter(id => !oldIds.includes(id));
        const deletedIds = oldIds.filter(id => !newIds.includes(id));

        const isUpdate = addedIds.length > 0 || deletedIds.length > 0;

        if (isUpdate) {
            const data = new FormData();
            data.append('addedIds', addedIds.toString());
            data.append('deletedIds', deletedIds.toString());
            const result = await fetch(`/api/previous-translations/update/${key.id}/${state.language}`, {
                method: "POST",
                body: data
            }).then(x => x.json());

            if (result.success) {
                const mappedPreviousTranslations = result.previousTranslations.map((previousTranslation) => {
                    return {
                        'value': previousTranslation.id,
                        'label': previousTranslation.original_text,
                        'localized_text': previousTranslation.localized_text
                    }
                });
                setPreviousTranslations(mappedPreviousTranslations);
                setPreviousTranslationsUpdate(mappedPreviousTranslations);
            }
        }
    }

    const handleTagsSave = async () => {
        const deletedTags = tagsUpdate.filter(comparer(tags));
        const addedTags = tags.filter(comparer(tagsUpdate));

        const mappedAddedTags = addedTags.map((t) => {
            let mapped = {
                name: t.label
            };
            if (!t.__isNew__) {
                mapped.id = t.value;
            }

            return mapped;
        });

        const mappedDeletedTags = deletedTags.map((t) => t.value);

        const isUpdate = mappedAddedTags.length > 0 || mappedDeletedTags.length > 0;

        if (isUpdate) {
            const data = new FormData();
            data.append('addedTags', JSON.stringify(mappedAddedTags));
            data.append('deletedTags', mappedDeletedTags.toString());
            const result = await fetch(`/api/tags/update/${key.id}`, {
                method: "POST",
                body: data
            }).then(x => x.json());

            if (result.success) {
                const mappedTags = result.tags.map((t) => {
                    return {
                        'value': t.id,
                        'label': t.name
                    }
                });
                setTags(mappedTags);
                setTagsUpdate(mappedTags);
                return true;
            }

            return false;
        }

        return true;
    }

    const handleIndicationSave = async (string) => {

        const data = new URLSearchParams();

        data.append("text", string);
        let result = {};
        if (value.indicationId) {
            result = await fetch(`/api/indication/update/${value.indicationId}`, {
                method: "PUT",
                body: data
            }).then(x => x.json());
        } else {
            data.append("translation_key_id", key.id);
            result = await fetch(`/api/indication/create`, {
                method: "POST",
                body: data
            }).then(x => x.json());
        }

        if (result.id) {
            setValue({
                ...value,
                indicationText: result.text,
                indicationId: result.id
            });
            setIndicationText(result.text);
        } else {
            setIndicationText(value.indicationText);
        }

    };


    const handleSaveComment = async (string, isPrivate) => {
        const data = new URLSearchParams();
        data.append("text", string);
        data.append("value_id", value.id);
        data.append("private", isPrivate);
        setSubmitLoader(true);

        await fetch(`/api/comment/create`, {
            method: "POST",
            body: data
        }).then(x => {
            x.json();
            getComments(value).then(commentsFeed => { setComments(commentsFeed) });
            setSubmitLoader(false);
        });
    };

    const handlePhotosSubmit = async (photos) => {
        const data = new FormData();
        data.append('key', key.id);
        Array.from(photos).forEach(photo => {
            data.append('photos[]', photo, photo.name);
        });

        const result = await fetch(`/api/image/upload`, {
            method: "POST",
            body: data
        }).then(x => x.json());

        if (result.success) {
            document.getElementById('photos').value = null;
            getImages().then(newImages => { setImages(newImages) });
        }
    }

    const handlePhotoDelete = async (id) => {
        const ok = window.confirm('Are you sure you want to delete this photo?');
        if (ok) {
            const result = await fetch(`/api/image/delete/${id}`, {
                method: "DELETE",
            }).then(x => x.json());

            if (result.success) {
                getImages().then(newImages => { setImages(newImages) });
            }
        }
    }

    const handleStatusChange = async (status) => {
        const data = new FormData();
        data.append('status', status);

        const result = await fetch(`/api/key/change-status/${key.id}`, {
            method: "POST",
            body: data
        }).then(x => x.json());

        if (result.id) {
            const { id, text, status } = result;
            dispatch({ type: 'SET_ACTIVE_KEY', activeKey: { id, text, status } });

            const keys = await getKeys(languageTag, KEY_STATUS.PUBLISHED);
            if(hasRole(roles, ROLE.ADMIN)) {
                const drafts = await getKeys(languageTag, KEY_STATUS.DRAFT);
                const deleted = await getKeys(languageTag, KEY_STATUS.DELETED);
                dispatch({ type: 'SET_DELETED', deleted });
                dispatch({ type: 'SET_DRAFTS', drafts });
            }
            dispatch({ type: 'SET_KEYS', keys });
            window.$('#confirm-status-change').modal("hide");
        }
    }

    const displayStatusChangeModal = (action) => {
        setActionToTake(action);
        window.$('#confirm-status-change').modal("show");
    }

    const getTranslationValueAudit = async (value) => {
        const audit = await fetch(`/api/value-audit/list/${value.id}`).then(x => x.json()).catch((e) => { return [] });
        return audit;
    }

    const getComments = async (value) => {
        if (value !== 'undefined') {
            let commentsFeed = await fetch(`/api/comment/view/${value.id}`).then(x => x.json()).catch((e) => { return [] });
            return commentsFeed;
        }
    }

    const getImages = async () => {
        const imgs = await fetch(`/api/image/view/${key.id}`).then(x => x.json());
        if (imgs.length > 0) {
            const newImages = imgs.map(image => { return { path: `${CONFIG.imgStorage}${image.path}`, id: image.id } });
            return newImages;
        }

        return [];
    }

    const getKeyValue = async () => {
        if (typeof key.id !== 'undefined' && key.id !== 0) {
            const newValue = await fetch(`/api/key/view/${key.id}/${state.language}`).then(x => x.json()).catch(e => console.log(e));
            return newValue;
        }
    }

    const getPreviousTranslations = async () => {
        if (typeof key.id !== 'undefined' && key.id !== 0) {
            const previousTranslations = await fetch(`/api/previous-translations/view/${key.id}/${state.language}`).then(x => x.json()).catch(e => console.log(e));
            return previousTranslations;
        }
    }

    const getTags = async () => {
        if (typeof key.id !== 'undefined' && key.id !== 0) {
            const tags = await fetch(`/api/tags/view/${key.id}`).then(x => x.json()).catch(e => console.log(e));
            return tags;
        }
    }


    return (
        <div className="col">
            <div className={"card-body p-0"}>
                <div className={"row"}>
                    {hasRole(roles, ROLE.ADMIN) &&
                        <div className="col-12 mb-4">
                            {(key.status == KEY_STATUS.DRAFT.toString() || key.status == KEY_STATUS.DELETED.toString()) && <button className="btn btn-mondly btn-blue" onClick={() => displayStatusChangeModal(KEY_OPERATIONS.PUBLISH)}>Publish key</button>}
                            {key.status == KEY_STATUS.PUBLISHED.toString() && <button className="btn btn-mondly btn-blue" onClick={() => displayStatusChangeModal(KEY_OPERATIONS.DRAFT)}>Mark as draft</button>}
                            {key.status == KEY_STATUS.PUBLISHED.toString() && <button className="btn btn-mondly btn-outline-blue ml-2" onClick={() => displayStatusChangeModal(KEY_OPERATIONS.OBSOLETE)}>Mark as obsolete</button>}
                        </div>
                    }
                </div>
                <div className="row details-panel flex-fill">
                    <div className={"col-lg-8 col-md-7"}>
                        <TranslationItemDetails label={"Indications"} originalText={indicationText ? indicationText : ''} text={value.displayIndicationText} handleSubmit={handleIndicationSave} />
                        <PreviousTranslationsList previousTranslations={previousTranslations} />
                        <TranslationForm sourceText={sourceText} targetText={text} charLimit={value.charLimit} lastTranslation={value.lastTranslation} status={value.status} auditList={auditList} handleSubmit={handleSave} error={error} />
                        <CommentSection comments={comments} handlaSubmit={handleSaveComment} isLoading={submitLoader} />
                        {hasRole(roles, ROLE.ADMIN) && <PreviousTranslations selectedItems={previousTranslations} onChange={(values) => { setPreviousTranslationsUpdate(values) }} onSave={handlePreviousTranslationsSave} />}
                        {hasRole(roles, ROLE.ADMIN) && <TagsSelect selectedItems={tags} showLabel={true} onChange={(values) => { setTags(values) }} onSave={handleTagsSave} />}
                    </div>
                    <div className={"col-lg-4 col-md-5 d-flex flex-column align-items-end"}>
                        <div className="translation-key-title text-right">
                            {value.id && <span>{hasRole(roles, ROLE.ADMIN) ? key.text : `KEY NO: ${value.translation_key_id}`}</span>}
                        </div>
                        <TranslationCarousel images={images} handleDelete={handlePhotoDelete} handleUpload={handlePhotosSubmit} />
                    </div>
                </div>
            </div>
            <div className="modal fade" id="confirm-status-change" tabIndex="-1" role="dialog" aria-hidden="true">
                <div className="modal-lg modal-dialog modal-dialog-centered modal-dialog-scrollable" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-12">
                                    {actionToTake === KEY_OPERATIONS.PUBLISH && <p>Are you sure you want to publish this key?</p>}
                                    {actionToTake === KEY_OPERATIONS.DRAFT && <p>Are you sure you want to mark this key as draft?</p>}
                                    {actionToTake === KEY_OPERATIONS.OBSOLETE && <p>Are you sure you want to mark this key as obsolete?</p>}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-mondly btn-outline-blue" data-dismiss="modal" aria-label="Close">Cancel</button>
                            {actionToTake === KEY_OPERATIONS.PUBLISH && <button className="btn btn-mondly btn-blue" onClick={() => handleStatusChange(KEY_STATUS.PUBLISHED)}>Publish key</button>}
                            {actionToTake === KEY_OPERATIONS.DRAFT && <button className="btn btn-mondly btn-blue" onClick={() => handleStatusChange(KEY_STATUS.DRAFT)}>Mark as draft</button>}
                            {actionToTake === KEY_OPERATIONS.OBSOLETE && <button className="btn btn-mondly btn-blue" onClick={() => handleStatusChange(KEY_STATUS.DELETED)}>Mark as obsolete</button>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
